/**
 * Listen to a live call — listen-only, from the Live calls page.
 *
 *   GET {operations}/api/voice/live-calls/:callControlId/listen   (SSE)
 *     event: state  { state: 'connecting' | 'live' | 'ended' | 'unavailable', reason? }
 *
 * Only an explicit `state: ended` (or a 404 for a call already gone) means the
 * call's audio ended. A stream that simply closes — a deploy, a proxy timeout,
 * a network blip — is a transport failure, reported as `error` so the page
 * offers "Listen again" instead of saying the call is over.
 *     event: audio  { seq, a }   base64 µ-law, 8 kHz mono, 100 ms, both sides mixed
 *
 * Starts only from `start()`, which must run inside a click: browsers keep
 * audio silent until the user asks for it. There is no automatic reconnect —
 * listening costs a Telnyx stream, so a dropped connection offers "Listen again"
 * rather than quietly restarting one. Stops on `stop()`, on unmount, and when
 * the call id changes.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/stores/auth.store';
import { getActiveTenantId } from '@/lib/activeTenant';
import { parseSseBlocks } from '@/hooks/voip/useLiveCallsStream';
import { createListenPlayer, type ListenPlayer } from '@/lib/calls/liveListenAudio';

export type ListenStatus =
  | 'idle'
  | 'connecting'
  | 'live'
  | 'ended'
  | 'unavailable'
  | 'forbidden'
  | 'error';

export interface UseLiveCallListenResult {
  status: ListenStatus;
  /** Why listening is unavailable, when the server said. */
  reason: string | null;
  start: () => void;
  stop: () => void;
  volume: number;
  setVolume: (value: number) => void;
}

function operationsBaseUrl(): string {
  const url = import.meta.env.VITE_OPERATIONS_URL ?? '';
  return typeof url === 'string' ? url.replace(/\/+$/, '') : '';
}

export function useLiveCallListen(
  callControlId: string | null | undefined,
  { createPlayer = createListenPlayer }: { createPlayer?: () => ListenPlayer } = {},
): UseLiveCallListenResult {
  const [status, setStatus] = useState<ListenStatus>('idle');
  const [reason, setReason] = useState<string | null>(null);
  const [volume, setVolumeState] = useState(1);
  const session = useRef<{ abort: AbortController; player: ListenPlayer } | null>(null);
  const volumeRef = useRef(volume);
  volumeRef.current = volume;

  const teardown = useCallback(() => {
    const current = session.current;
    session.current = null;
    if (!current) return;
    current.abort.abort();
    void current.player.close();
  }, []);

  const stop = useCallback(() => {
    teardown();
    setStatus('idle');
    setReason(null);
  }, [teardown]);

  const start = useCallback(() => {
    if (!callControlId || session.current) return;
    const token = useAuthStore.getState().token;
    if (!token) {
      setStatus('forbidden');
      return;
    }

    let player: ListenPlayer;
    try {
      player = createPlayer();
    } catch {
      setStatus('error');
      return;
    }
    player.setVolume(volumeRef.current);
    const abort = new AbortController();
    const mine = { abort, player };
    session.current = mine;
    setStatus('connecting');
    setReason(null);

    const finish = (next: ListenStatus, why: string | null = null) => {
      if (session.current !== mine) return;
      teardown();
      setStatus(next);
      setReason(why);
    };

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      Accept: 'text/event-stream',
      'Cache-Control': 'no-cache',
    };
    const tenantId = getActiveTenantId();
    if (tenantId) headers['x-tenant-id'] = tenantId;

    void (async () => {
      try {
        const response = await fetch(
          `${operationsBaseUrl()}/api/voice/live-calls/${encodeURIComponent(callControlId)}/listen`,
          { headers, signal: abort.signal, credentials: 'omit', cache: 'no-store' },
        );
        if (response.status === 401 || response.status === 403) return finish('forbidden');
        if (response.status === 404) return finish('ended');
        if (!response.ok || !response.body) return finish('error');

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (!abort.signal.aborted) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parsed = parseSseBlocks(buffer);
          buffer = parsed.rest;
          for (const { event, data } of parsed.events) {
            let payload: Record<string, unknown>;
            try {
              payload = JSON.parse(data) as Record<string, unknown>;
            } catch {
              continue;
            }
            if (event === 'audio' && typeof payload.a === 'string') {
              if (session.current === mine) player.play(payload.a);
            } else if (event === 'state') {
              const state = payload.state;
              if (state === 'live' || state === 'connecting') {
                if (session.current === mine) setStatus(state);
              } else if (state === 'ended') {
                return finish('ended');
              } else if (state === 'unavailable') {
                return finish(
                  'unavailable',
                  typeof payload.reason === 'string' ? payload.reason : null,
                );
              }
            }
          }
        }
        // Closed without saying why: the connection dropped, not the call.
        if (!abort.signal.aborted) finish('error');
      } catch {
        if (!abort.signal.aborted) finish('error');
      }
    })();
  }, [callControlId, createPlayer, teardown]);

  const setVolume = useCallback((value: number) => {
    setVolumeState(value);
    session.current?.player.setVolume(value);
  }, []);

  // A different call, or leaving the page, ends any listening in progress.
  useEffect(() => {
    return () => {
      teardown();
      setStatus('idle');
      setReason(null);
    };
  }, [callControlId, teardown]);

  return { status, reason, start, stop, volume, setVolume };
}
